import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, X, Package, Check, Clock, Snowflake, Boxes, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardAlerts = () => {
  const [alerts, setAlerts] = useState(null);
  const [smart, setSmart] = useState(null);
  const [awaitingConf, setAwaitingConf] = useState(null); // {count, overdue_count, items}
  const [dismissed, setDismissed] = useState(false);
  const [actionItem, setActionItem] = useState(null); // {id, type: 'pay'|'receive', description, amount}
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    axios.get(`${API}/dashboard/alerts?days_ahead=3`, { withCredentials: true })
      .then(r => setAlerts(r.data))
      .catch(() => {});
    axios.get(`${API}/inventory/alerts?stale_days=60`, { withCredentials: true })
      .then(r => setSmart(r.data))
      .catch(() => {});
    axios.get(`${API}/cash-sessions/awaiting-conference/summary`, { withCredentials: true })
      .then(r => setAwaitingConf(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  if (dismissed) return null;

  const {
    payables_due_soon = [], payables_overdue = [],
    receivables_due_soon = [], receivables_overdue = [],
    low_stock = [], totals = {},
  } = alerts || {};

  const totalAlerts =
    payables_due_soon.length + payables_overdue.length +
    receivables_due_soon.length + receivables_overdue.length +
    low_stock.length +
    (smart?.stale_count || 0) + (smart?.overstock_count || 0);

  const awaitingCount = awaitingConf?.count || 0;
  const overdueConfCount = awaitingConf?.overdue_count || 0;

  if (totalAlerts === 0 && awaitingCount === 0) return null;

  const daysFromNow = (iso) => {
    const days = Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return `Vencido há ${Math.abs(days)} dia(s)`;
    if (days === 0) return 'Vence hoje';
    return `Vence em ${days} dia(s)`;
  };

  const openAction = (item, type) => {
    setActionItem({ ...item, type });
    setPaymentMethod('dinheiro');
    setError('');
  };

  const confirmAction = async () => {
    if (!actionItem) return;
    setLoading(true);
    setError('');
    try {
      const url = actionItem.type === 'pay'
        ? `${API}/accounts/payables/${actionItem.id}/pay`
        : `${API}/accounts/receivables/${actionItem.id}/receive`;
      await axios.post(url, { payment_method: paymentMethod }, { withCredentials: true });
      setSuccess(`${actionItem.type === 'pay' ? 'Pagamento' : 'Recebimento'} confirmado!`);
      setActionItem(null);
      load();
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao processar');
    } finally {
      setLoading(false);
    }
  };

  const renderItemRow = (item, type, colorScheme) => (
    <div key={item.id} className="text-sm flex items-center justify-between py-2 border-t first:border-t-0" style={{ borderColor: colorScheme.border }}>
      <div className="flex-1 min-w-0">
        <div className="text-slate-800 truncate">{item.party_name} - {item.description}</div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-xs ${colorScheme.dateText} font-medium`}>{daysFromNow(item.due_date)}</span>
          <span className="text-xs font-bold text-slate-700">R$ {item.amount.toFixed(2)}</span>
        </div>
      </div>
      <button
        onClick={() => openAction(item, type)}
        className={`ml-3 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${colorScheme.btn}`}
        data-testid={`quick-${type}-${item.id}`}
      >
        <Check size={14} /> {type === 'pay' ? 'Pagar' : 'Receber'}
      </button>
    </div>
  );

  return (
    <>
      {/* Turnos aguardando conferência (novo fluxo) */}
      {awaitingCount > 0 && (
        <div
          className={`rounded-md border-l-4 border-y border-r p-4 mb-4 ${overdueConfCount > 0 ? 'bg-red-50 border-red-500' : 'bg-indigo-50 border-indigo-500'}`}
          data-testid="awaiting-conference-banner"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-md ${overdueConfCount > 0 ? 'bg-red-600' : 'bg-indigo-600'}`}>
                <ShieldCheck className="text-white" size={22} />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {awaitingCount} turno(s) aguardando conferência
                  {overdueConfCount > 0 && (
                    <span className="ml-2 text-xs font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 rounded" data-testid="awaiting-overdue-badge">
                      {overdueConfCount} atrasado(s) &gt; 24h
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-600 mt-0.5">
                  Turnos encerrados pelo PDV aguardando você (gerente) fazer a conferência dos valores contados.
                </p>
              </div>
            </div>
            <Link
              to="/cash-session"
              className={`px-4 py-2 rounded-md text-sm font-bold text-white ${overdueConfCount > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              data-testid="awaiting-conference-link"
            >
              Conferir agora <ArrowUpRight size={14} className="inline ml-1" />
            </Link>
          </div>
          {overdueConfCount > 0 && awaitingConf?.items && (
            <ul className="mt-2 space-y-1 text-xs">
              {awaitingConf.items.filter(i => i.is_overdue).slice(0, 3).map((i) => (
                <li key={i.id} className="text-red-800" data-testid={`overdue-conf-${i.id}`}>
                  ⏰ <strong>{i.user_name}</strong> — encerrou há {Math.round(i.age_hours)}h {i.terminal && `· ${i.terminal}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {totalAlerts > 0 && (
      <div className="bg-white rounded-md border-l-4 border-red-600 border-y border-r border-slate-200 p-6 mb-8" data-testid="dashboard-alerts">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-md">
              <AlertTriangle className="text-red-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Alertas Pendentes</h2>
              <p className="text-sm text-slate-500">{totalAlerts} item(s) requer(em) sua atenção</p>
            </div>
          </div>
          <button onClick={() => setDismissed(true)} className="text-slate-400 hover:text-slate-600" data-testid="dismiss-alerts">
            <X size={20} />
          </button>
        </div>

        {success && <Alert className="mb-4 border-green-600 bg-green-50"><AlertDescription className="text-green-700 font-medium">{success}</AlertDescription></Alert>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(payables_overdue.length > 0 || payables_due_soon.length > 0) && (
            <div className="border border-slate-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Clock size={18} className="text-yellow-600" /> Contas a Pagar
                </h3>
                <Link to="/accounts" className="text-blue-600 text-sm hover:underline flex items-center gap-1" data-testid="link-payables">
                  Ver todas <ArrowUpRight size={14} />
                </Link>
              </div>

              {payables_overdue.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-red-700">Vencidos</span>
                    <span className="text-sm font-bold text-red-700">R$ {totals.payables_overdue_amount.toFixed(2)}</span>
                  </div>
                  {payables_overdue.slice(0, 3).map(p => renderItemRow(p, 'pay', {
                    border: '#fecaca',
                    dateText: 'text-red-700',
                    btn: 'bg-red-600 hover:bg-red-700 text-white'
                  }))}
                  {payables_overdue.length > 3 && <p className="text-xs text-red-600 mt-1">+ {payables_overdue.length - 3} outro(s)</p>}
                </div>
              )}

              {payables_due_soon.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-yellow-700">Próximos 3 dias</span>
                    <span className="text-sm font-bold text-yellow-700">R$ {totals.payables_due_amount.toFixed(2)}</span>
                  </div>
                  {payables_due_soon.slice(0, 3).map(p => renderItemRow(p, 'pay', {
                    border: '#fef08a',
                    dateText: 'text-yellow-700',
                    btn: 'bg-yellow-500 hover:bg-yellow-600 text-slate-900'
                  }))}
                  {payables_due_soon.length > 3 && <p className="text-xs text-yellow-700 mt-1">+ {payables_due_soon.length - 3} outro(s)</p>}
                </div>
              )}
            </div>
          )}

          {(receivables_overdue.length > 0 || receivables_due_soon.length > 0) && (
            <div className="border border-slate-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Clock size={18} className="text-blue-600" /> Contas a Receber
                </h3>
                <Link to="/accounts" className="text-blue-600 text-sm hover:underline flex items-center gap-1" data-testid="link-receivables">
                  Ver todas <ArrowUpRight size={14} />
                </Link>
              </div>

              {receivables_overdue.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-red-700">Vencidos</span>
                    <span className="text-sm font-bold text-red-700">R$ {totals.receivables_overdue_amount.toFixed(2)}</span>
                  </div>
                  {receivables_overdue.slice(0, 3).map(r => renderItemRow(r, 'receive', {
                    border: '#fecaca',
                    dateText: 'text-red-700',
                    btn: 'bg-green-600 hover:bg-green-700 text-white'
                  }))}
                  {receivables_overdue.length > 3 && <p className="text-xs text-red-600 mt-1">+ {receivables_overdue.length - 3} outro(s)</p>}
                </div>
              )}

              {receivables_due_soon.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-700">Próximos 3 dias</span>
                    <span className="text-sm font-bold text-blue-700">R$ {totals.receivables_due_amount.toFixed(2)}</span>
                  </div>
                  {receivables_due_soon.slice(0, 3).map(r => renderItemRow(r, 'receive', {
                    border: '#bfdbfe',
                    dateText: 'text-blue-700',
                    btn: 'bg-green-600 hover:bg-green-700 text-white'
                  }))}
                  {receivables_due_soon.length > 3 && <p className="text-xs text-blue-700 mt-1">+ {receivables_due_soon.length - 3} outro(s)</p>}
                </div>
              )}
            </div>
          )}

          {low_stock.length > 0 && (
            <div className="border border-slate-200 rounded-md p-4 md:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Package size={18} className="text-red-600" /> Estoque Baixo ({low_stock.length})
                </h3>
                <Link to="/products" className="text-blue-600 text-sm hover:underline flex items-center gap-1" data-testid="link-low-stock">
                  Ver produtos <ArrowUpRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {low_stock.slice(0, 6).map(p => (
                  <div key={p.id} className="bg-red-50 border border-red-200 rounded p-2 text-sm flex items-center justify-between">
                    <span className="font-medium text-slate-900 truncate">{p.name}</span>
                    <span className="text-red-700 font-bold ml-2 whitespace-nowrap">{p.stock_quantity} {p.unit}</span>
                  </div>
                ))}
              </div>
              {low_stock.length > 6 && <p className="text-xs text-slate-500 mt-2">+ {low_stock.length - 6} outro(s) produto(s)</p>}
            </div>
          )}

          {/* Produtos PARADOS (sem venda há X dias) */}
          {smart && smart.stale_count > 0 && (
            <div className="border border-slate-200 rounded-md p-4" data-testid="stale-products-alert">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Snowflake size={18} className="text-blue-600" /> Produtos Parados ({smart.stale_count})
                </h3>
                <Link to="/products" className="text-blue-600 text-sm hover:underline flex items-center gap-1">
                  Ver todos <ArrowUpRight size={14} />
                </Link>
              </div>
              <p className="text-xs text-slate-500 mb-2">Sem vendas há {smart.stale_days}+ dias</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {smart.stale.slice(0, 5).map(p => (
                  <div key={p.id} className="bg-blue-50 border border-blue-200 rounded p-2 text-sm flex items-center justify-between">
                    <span className="font-medium text-slate-900 truncate">{p.name}</span>
                    <span className="text-xs font-bold text-blue-700">{p.days_without_sale != null ? `${p.days_without_sale}d` : 'nunca'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Capital parado (overstock) */}
          {smart && smart.overstock_count > 0 && (
            <div className="border border-slate-200 rounded-md p-4" data-testid="overstock-alert">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <Boxes size={18} className="text-amber-600" /> Excesso de Estoque ({smart.overstock_count})
                </h3>
              </div>
              <p className="text-xs text-slate-500 mb-2">Mais de 5x o estoque mínimo (capital parado)</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {smart.overstock.slice(0, 5).map(p => (
                  <div key={p.id} className="bg-amber-50 border border-amber-200 rounded p-2 text-sm flex items-center justify-between">
                    <span className="font-medium text-slate-900 truncate">{p.name}</span>
                    <span className="text-xs font-bold text-amber-700">R$ {p.capital_locked?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Modal de ação rápida (Pagar/Receber) */}
      {actionItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...overlayClose(() => setActionItem(null))}>
          <div className="bg-white rounded-lg p-6 w-96" onClick={(e) => e.stopPropagation()} data-testid="quick-action-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              {actionItem.type === 'pay' ? 'Pagar Conta' : 'Receber Conta'}
            </h3>
            <p className="text-sm text-slate-600 mb-1">{actionItem.party_name}</p>
            <p className="text-sm text-slate-500 mb-4">{actionItem.description}</p>
            <div className="bg-slate-50 rounded p-3 mb-4 flex justify-between items-center">
              <span className="text-xs uppercase tracking-wider text-slate-500">Valor</span>
              <span className="text-2xl font-bold text-blue-600">R$ {actionItem.amount.toFixed(2)}</span>
            </div>
            <div className="space-y-2 mb-4">
              <Label>Forma de Pagamento *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="quick-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                  <SelectItem value="pix">PIX Loja</SelectItem>
                  <SelectItem value="pix_maquininha">PIX na Maquininha</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setActionItem(null)} className="flex-1">Cancelar</Button>
              <Button
                onClick={confirmAction}
                disabled={loading}
                className={`flex-1 ${actionItem.type === 'pay' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                data-testid="confirm-quick-action"
              >
                {loading ? 'Processando...' : `Confirmar ${actionItem.type === 'pay' ? 'Pagamento' : 'Recebimento'}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
